// Prospects (brief section 9): potential clients, kept apart from competitors. Sending is manual.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { CountrySelect } from '../../components/CountrySelect';
import { Badge, Empty, ErrorMsg, Loading, Modal, PageHead, Time, Unknown } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { countryName } from '../../lib/format';
import { PROSPECT_KIND, PROSPECT_STATUS, STATUS_TEXT, type ProspectList, type ProspectRow } from '../../lib/prospectTypes';
import { useApi } from '../../lib/session';

function NewProspect({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const [f, setF] = useState({ name: '', kind: 'architect', website: '', country: '', city: '', relevant_project: '', fit_reason: '' });
  const [competitor, setCompetitor] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || null]));
      const p = await api<ProspectRow>('/api/prospects', { body: { ...body, allow_competitor_domain: competitor } });
      nav(`/prospects/${p.id}`);
    } catch (e2) {
      setErr(errorText(e2));
    } finally {
      setBusy(false);
    }
  }
  const isCompetitorError = /competitor directory/.test(err);
  return (
    <Modal
      title="New prospect"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="new-prospect" className="btn primary" disabled={busy || !f.name.trim()}>
            Create
          </button>
        </>
      }
    >
      <form id="new-prospect" className="form-grid" onSubmit={submit} aria-label="New prospect">
        <label className="field">
          <span>Name</span>
          <input value={f.name} onChange={set('name')} required />
        </label>
        <label className="field">
          <span>Kind</span>
          <select value={f.kind} onChange={set('kind')}>
            {PROSPECT_KIND.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Website</span>
          <input value={f.website} onChange={set('website')} placeholder="atelier.fr" />
        </label>
        <label className="field">
          <span>Country</span>
          <CountrySelect value={f.country} onChange={(country) => setF({ ...f, country })} />
        </label>
        <label className="field">
          <span>City</span>
          <input value={f.city} onChange={set('city')} />
        </label>
        <label className="field span2">
          <span>Relevant project (a fact you checked)</span>
          <textarea rows={2} value={f.relevant_project} onChange={set('relevant_project')} />
        </label>
        <label className="field span2">
          <span>Why they fit</span>
          <textarea rows={2} value={f.fit_reason} onChange={set('fit_reason')} />
        </label>
        {isCompetitorError && (
          <label className="check block span2">
            <input type="checkbox" checked={competitor} onChange={(e) => setCompetitor(e.target.checked)} /> This company is in the competitor directory,
            and it is also a prospective client
          </label>
        )}
      </form>
      <ErrorMsg>{err}</ErrorMsg>
    </Modal>
  );
}

export function Prospects() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const due = sp.get('due') === '1';
  const q = sp.get('q') ?? '';
  const query = new URLSearchParams();
  if (status) query.set('status', status);
  if (due) query.set('due', 'true');
  if (q) query.set('q', q);
  const list = useApi<ProspectList>(`/api/prospects?${query.toString()}`);
  const [creating, setCreating] = useState(false);

  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };

  return (
    <div className="page">
      <PageHead title="Prospects" meta="Potential clients · sending stays manual">
        <button type="button" className="btn primary small" onClick={() => setCreating(true)}>
          New prospect
        </button>
      </PageHead>
      <p className="small muted">
        Architects, developers and agencies Mirage could work for. They are separate from competitors: nothing in the competitor directory becomes a
        lead by itself. The app prepares drafts and records what you did; it never sends an email.
      </p>
      <div className="filters row gap wrap">
        <label className="small">
          Status{' '}
          <select aria-label="Status" value={status} onChange={(e) => setParam('status', e.target.value)}>
            <option value="">all</option>
            {PROSPECT_STATUS.map((s) => (
              <option key={s} value={s}>
                {STATUS_TEXT[s]} {list.data?.by_status[s] ? `(${list.data.by_status[s]})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="check small">
          <input type="checkbox" checked={due} onChange={(e) => setParam('due', e.target.checked ? '1' : '')} /> follow-up due
          {list.data ? ` (${list.data.follow_ups_due})` : ''}
        </label>
        <input className="search" placeholder="Search name or domain" aria-label="Search" defaultValue={q} onKeyDown={(e) => e.key === 'Enter' && setParam('q', (e.target as HTMLInputElement).value)} />
      </div>
      {list.loading && !list.data ? (
        <Loading what="Loading prospects" />
      ) : !list.data ? (
        <ErrorMsg>{list.error}</ErrorMsg>
      ) : list.data.items.length === 0 ? (
        <Empty>{status || due || q ? 'No prospect matches these filters.' : 'No prospect yet. Add the first one with “New prospect”.'}</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Prospect</th>
                <th>Kind</th>
                <th>Where</th>
                <th>Status</th>
                <th>Next action</th>
                <th>Last interaction</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link to={`/prospects/${p.id}`}>{p.name}</Link>
                    {p.primary_domain && <div className="muted small">{p.primary_domain}</div>}
                    {p.competitor_studio_id && <Badge tone="warn">also a competitor</Badge>}
                  </td>
                  <td className="small">{p.kind}</td>
                  <td className="small">{[p.city, p.country && countryName(p.country)].filter(Boolean).join(', ') || <Unknown />}</td>
                  <td>
                    <Badge>{STATUS_TEXT[p.status] ?? p.status}</Badge>
                  </td>
                  <td className="small">
                    {p.next_action ?? <span className="muted">none</span>}
                    {p.next_action_due && (
                      <div className={p.overdue ? 'err-text' : 'muted'}>
                        {p.overdue ? 'due ' : 'by '}
                        {p.next_action_due}
                      </div>
                    )}
                  </td>
                  <td className="small">{p.last_interaction_at ? <Time iso={p.last_interaction_at} /> : <span className="muted">none</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <NewProspect onClose={() => setCreating(false)} />}
    </div>
  );
}
